import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Button,
  CircularProgress,
  Avatar,
  IconButton,
  Pagination,
  useTheme,
  useMediaQuery,
  Chip,
  Backdrop,
} from "@mui/material";
import { AccessTime, Reply, Add, Visibility } from "@mui/icons-material";
import TaskDetail from "./UI/reusable-components/TaskComponents/TaskDetail";
import CreateTask from "./UI/reusable-components/TaskComponents/CreateTask";
import useAlert from "../hooks/useAlert";
import UserTaskService from "../services/userTaskService";
import { useSelector } from "react-redux";

// Task durumları için renk ve açıklamalar
const taskStateConfig = {
  0: { label: "Beklemede", color: "#757575", bgColor: "#F5F5F5" },
  1: { label: "Yeni Destek Talebi", color: "#2196F3", bgColor: "#E3F2FD" },
  2: { label: "Destek Ekibi İnceliyor", color: "#9C27B0", bgColor: "#F3E5F5" },
  3: { label: "Yanıt Bekliyor", color: "#FF9800", bgColor: "#FFF3E0" },
  4: {
    label: "Kullanıcı Yanıtı Bekleniyor",
    color: "#F44336",
    bgColor: "#FFEBEE",
  },
  5: { label: "Çözüldü", color: "#4CAF50", bgColor: "#E8F5E9" },
  6: { label: "Kapatıldı", color: "#607D8B", bgColor: "#ECEFF1" },
  7: { label: "İptal Edildi", color: "#9E9E9E", bgColor: "#F5F5F5" },
};

export const UserTasks = () => {
  const { showSuccessAlertAll, showErrorAlertAll } = useAlert();
  const [tasks, setTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [totalCount, setTotalCount] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const userId = useSelector((state) => state?.auth?.user?.id);

  const handleOpenDetailModal = async (task) => {
    try {
      setModalLoading(true);
      const taskDetails = await UserTaskService.getUserTaskById(task.id);
      if (!taskDetails || !taskDetails.id) {
        showErrorAlertAll("Talep detayları alınamadı.");
        return;
      }

      const subTasks = await UserTaskService.getSubTasks(task.id);

      const processedTask = {
        ...task,
        ...taskDetails,
        id: task.id,
        userId: task.userId || taskDetails.userId,
        title: taskDetails.title || task.title || "Başlıksız Talep",
        detail: taskDetails.detail || task.detail || "",
        content:
          taskDetails.detail ||
          taskDetails.content ||
          task.detail ||
          task.content ||
          "", // Add content field
        state: taskDetails.state ?? task.state ?? 0,
        createDate:
          taskDetails.createDate || task.createDate || new Date().toISOString(),
        updateDate:
          taskDetails.updateDate ||
          task.updateDate ||
          taskDetails.createDate ||
          new Date().toISOString(),
        ia: taskDetails.ia ?? task.ia ?? true,
        userName: taskDetails.userName || task.userName || "Kullanıcı",
        user: taskDetails.user ||
          task.user || {
            id: task.userId,
            name: taskDetails.userName || task.userName || "Kullanıcı",
          },
        replyCount: taskDetails.replyCount ?? task.replyCount ?? 0,
        subTasks: subTasks || [],
      };

      setSelectedTask(processedTask);
      setModalLoading(false);
      setDetailModalOpen(true);
    } catch (err) {
      setModalLoading(false);
      console.error("Error loading task details:", err);
      showErrorAlertAll("Talep detayları yüklenirken bir hata oluştu.");
    }
  };

  const handleCloseDetailModal = () => setDetailModalOpen(false);

  const handleOpenCreateModal = () => setCreateModalOpen(true);
  const handleCloseCreateModal = () => setCreateModalOpen(false);

  const getTasks = async () => {
    if (!userId) {
      setAllTasks([]);
      setTasks([]);
      setTotalCount(0);
      return;
    }

    try {
      setLoading(true);
      const response = await UserTaskService.fetchUserTasks(userId);

      if (response && response.items) {
        const processedTasks = response.items
          .filter(
            (task) =>
              task.title &&
              task.title.trim() !== "" &&
              task.ia !== false &&
              (!task.parentId || task.parentId === 0) &&
              (task.sub_task === undefined || task.sub_task === 0) &&
              task.userId === userId // Ekstra güvenlik kontrolü
          )
          .sort((a, b) => new Date(b.createDate) - new Date(a.createDate));

        setAllTasks(processedTasks);
        setTotalCount(processedTasks.length);

        // Sayfalama işlemi
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedTasks = processedTasks.slice(startIndex, endIndex);

        setTasks(paginatedTasks);
      } else {
        setAllTasks([]);
        setTasks([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setAllTasks([]);
      setTasks([]);
      setTotalCount(0);
      showErrorAlertAll("Talepler yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await UserTaskService.deleteUserTask(taskId);
      setAllTasks((prevTasks) =>
        prevTasks.filter((task) => task.id !== taskId)
      );
      setTotalCount((prev) => prev - 1);
      // Mevcut sayfadaki gösterilen taskları güncelle
      const startIndex = (page - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      setTasks(
        allTasks
          .filter((task) => task.id !== taskId)
          .slice(startIndex, endIndex)
      );

      showSuccessAlertAll("Talep başarıyla silindi.");
      if (detailModalOpen && selectedTask?.id === taskId) {
        setDetailModalOpen(false);
      }
    } catch (err) {
      console.error("Error deleting task:", err);
      showErrorAlertAll("Talep silinirken bir hata oluştu.");
    }
  };

  useEffect(() => {
    if (userId) {
      getTasks();
    } else {
      setAllTasks([]);
      setTasks([]);
      setTotalCount(0);
    }
  }, [userId]); // userId değiştiğinde taskları yeniden yükle

  const handleTaskCreated = async (taskData) => {
    try {
      await UserTaskService.createUserTask({
        userId,
        ...taskData,
      });
      handleCloseCreateModal();
      getTasks();
      showSuccessAlertAll("Talep başarıyla oluşturuldu.");
    } catch (err) {
      console.error("Error creating task:", err);
      showErrorAlertAll("Talep oluşturulurken bir hata oluştu.");
    }
  };

  const handleTaskUpdated = () => {
    handleCloseDetailModal();
    getTasks();
    showSuccessAlertAll("Talep başarıyla güncellendi.");
  };

  const handleTaskDeleted = () => {
    if (selectedTask) {
      handleDeleteTask(selectedTask.id);
    }
    handleCloseDetailModal();
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", bgcolor: "#F8FAFC", p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={modalLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              letterSpacing: "-0.5px",
              fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
            }}
          >
            Talepler
          </Typography>
          <Chip
            label={`${totalCount} talep`}
            size="small"
            sx={{
              bgcolor: "#E3F2FD",
              color: "#1565C0",
              fontWeight: 600,
              height: "24px",
            }}
          />
        </Box>

        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenCreateModal}
          sx={{
            bgcolor: "#1A237E",
            "&:hover": { bgcolor: "#0D47A1" },
            textTransform: "none",
            px: { xs: 2, sm: 3 },
            py: { xs: 1, sm: "auto" },
            whiteSpace: "nowrap",
            width: { xs: "100%", sm: "auto" },
          }}
        >
          Yeni Talep
        </Button>
      </Box>

      {/* Table Header */}
      {!loading && tasks.length > 0 && (
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            bgcolor: "#F1F5F9",
            borderRadius: "12px 12px 0 0",
            p: 2,
            mb: 0.5,
            borderBottom: "2px solid #E2E8F0",
          }}
        >
          <Typography sx={{ flex: 3, color: "#475569", fontWeight: 600 }}>
            Başlık
          </Typography>
          <Typography sx={{ flex: 2, color: "#475569", fontWeight: 600 }}>
            Durum
          </Typography>
          <Typography sx={{ flex: 2, color: "#475569", fontWeight: 600 }}>
            Tarih
          </Typography>
          <Box
            sx={{
              width: 120,
              textAlign: "center",
              color: "#475569",
              fontWeight: 600,
            }}
          >
            İşlemler
          </Box>
        </Box>
      )}

      {/* Task List */}
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
        >
          <CircularProgress />
        </Box>
      ) : tasks.length === 0 ? (
        <Card
          sx={{
            textAlign: "center",
            py: { xs: 4, sm: 6, md: 8 },
            px: { xs: 2, sm: 3 },
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              bgcolor: "#F5F5F5",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
              mb: 2,
            }}
          >
            <AccessTime sx={{ fontSize: 32, color: "#9E9E9E" }} />
          </Box>
          <Typography variant="h6" color="text.secondary">
            Henüz talep bulunmamaktadır
          </Typography>
        </Card>
      ) : (
        <Box
          sx={{
            bgcolor: "white",
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          {tasks.map((task, index) => (
            <Box
              key={task?.id || index}
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "flex-start", md: "center" },
                p: { xs: 1.5, sm: 2 },
                borderBottom: "1px solid #E2E8F0",
                "&:last-child": { borderBottom: "none" },
                "&:hover": {
                  bgcolor: "#F8FAFC",
                  cursor: "pointer",
                },
                transition: "all 0.2s ease",
              }}
            >
              {/* Mobile View */}
              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  width: "100%",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 1,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: "#1E40AF",
                        fontSize: { xs: "0.9rem", sm: "1rem" },
                        mb: 0.5,
                      }}
                    >
                      {task.title}
                    </Typography>
                    {task.state && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            bgcolor:
                              taskStateConfig[task.state]?.color || "#757575",
                          }}
                        />
                        <Typography
                          sx={{
                            color:
                              taskStateConfig[task.state]?.color || "#757575",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                          }}
                        >
                          {taskStateConfig[task.state]?.label ||
                            "Bilinmeyen Durum"}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Chip
                    icon={<Reply sx={{ fontSize: 14 }} />}
                    label={task.replyCount || 0}
                    size="small"
                    sx={{
                      bgcolor: "#F1F5F9",
                      color: "#475569",
                      height: "24px",
                      "& .MuiChip-icon": { color: "#475569" },
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Avatar
                    src={task.user?.avatarUrl}
                    alt={task.user?.name}
                    sx={{ width: 24, height: 24 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#475569",
                      fontSize: "0.8rem",
                    }}
                  >
                    {task.userName || "Kullanıcı"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#64748B",
                      ml: "auto",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <AccessTime sx={{ fontSize: 14 }} />
                    {new Date(task.createDate).toLocaleString("tr-TR")}
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    color: "#475569",
                    fontSize: "0.85rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    mt: 0.5,
                  }}
                >
                  {task.detail}
                </Typography>
              </Box>

              {/* Desktop View */}
              <Typography
                sx={{
                  flex: 3,
                  display: { xs: "none", md: "block" },
                  color: "text.primary",
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  pr: 2,
                }}
              >
                {task.title}
              </Typography>

              <Box
                sx={{
                  flex: 2,
                  display: { xs: "none", md: "flex" },
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                {task.state && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor:
                          taskStateConfig[task.state]?.color || "#757575",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        color: taskStateConfig[task.state]?.color || "#757575",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    >
                      {taskStateConfig[task.state]?.label || "Bilinmeyen Durum"}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Typography
                sx={{
                  flex: 2,
                  color: "#64748B",
                  display: { xs: "none", md: "block" },
                }}
              >
                {new Date(task.createDate).toLocaleString("tr-TR")}
              </Typography>

              {/* Desktop View İşlemler Sütunu */}
              <Box
                sx={{
                  width: { xs: "auto", md: 120 },
                  display: { xs: "none", md: "flex" },
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleOpenDetailModal(task)}
                  sx={{
                    bgcolor: "#E3F2FD",
                    "&:hover": {
                      bgcolor: "#90CAF9",
                    },
                  }}
                >
                  <Visibility sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>

              {/* Mobile View İşlemler */}
              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  justifyContent: "flex-end",
                  alignItems: "center",
                  width: "100%",
                  mt: 1,
                  gap: 1,
                }}
              >
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleOpenDetailModal(task)}
                  sx={{
                    bgcolor: "#E3F2FD",
                    "&:hover": {
                      bgcolor: "#90CAF9",
                    },
                  }}
                >
                  <Visibility sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Pagination */}
      {totalCount > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: { xs: 2, sm: 3 },
            mb: { xs: 1, sm: 2 },
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size={isMobile ? "small" : "medium"}
            showFirstButton
            showLastButton
            siblingCount={isMobile ? 0 : 1}
            boundaryCount={isMobile ? 1 : 2}
            sx={{
              "& .MuiPaginationItem-root": {
                color: "#1A237E",
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                minWidth: { xs: 28, sm: 32 },
                height: { xs: 28, sm: 32 },
                "&.Mui-selected": {
                  bgcolor: "#1A237E",
                  color: "white",
                  "&:hover": {
                    bgcolor: "#0D47A1",
                  },
                },
              },
            }}
          />
        </Box>
      )}

      {/* Modals */}
      {selectedTask && (
        <TaskDetail
          open={detailModalOpen}
          onClose={handleCloseDetailModal}
          task={selectedTask}
          onTaskUpdated={handleTaskUpdated}
          onTaskDeleted={handleTaskDeleted}
        />
      )}

      <CreateTask
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        onTaskCreated={handleTaskCreated}
      />
    </Box>
  );
};

export default UserTasks;
